import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Info, 
  Download,
  Calendar,
  Ship,
  Plane,
  Target,
  AlertCircle,
  HelpCircle,
  Clock,
  PoundSterling
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import DaysInHand from '../components/DaysInHand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Eligibility = () => {
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEligibility();
  }, []);

  const fetchEligibility = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/eligibility/status`);
      setEligibility(response.data);
    } catch (err) {
      setError('Failed to load eligibility status');
    } finally {
      setLoading(false);
    }
  };

  const exportHS205 = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/eligibility/hs205-export`);
      const data = response.data;
      
      // Create CSV content
      let csv = 'Row,Date Left UK (A),Date Returned UK (B),Days Abroad (C),Days in UK (D),Total Days (E),Total UK Days (F),Half Total (G),Breach (H),Trip Name\n';
      data.rows.forEach(row => {
        csv += `${row.row_num},${row.date_left_uk},${row.date_returned_uk},${row.days_abroad},${row.days_in_uk},${row.total_days},${row.total_uk_days},${row.half_total},${row.breach ? 'YES' : 'NO'},${row.trip_name}\n`;
      });
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HS205_worksheet_${data.export_date}.csv`;
      a.click();
    } catch (err) {
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-6">
            <p className="text-red-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { current_period, completed_periods, hs205_rows, has_valid_claim, total_qualifying_earnings, warnings, next_steps, current_table_summary, claim_break_date, tax_year_claims } = eligibility;

  // Use current_table_summary for status cards (reflects the active sub-table after any restarts)
  const cts = current_table_summary || {};
  const totalDays = cts.total_days || 0;
  const ukDays = cts.uk_days || 0;
  const halfTotal = cts.half_total || 0;
  const progressTo365 = cts.progress_365 || 0;
  const halfDayBuffer = cts.buffer || 0;
  const halfDayUsed = halfTotal > 0 ? (ukDays / halfTotal) * 100 : 0;
  const tableRestarts = cts.table_restart_count || 0;
  const periodStart = cts.period_start;
  const hasBreach = cts.has_breach || false;
  const cbd = claim_break_date;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">SED Eligibility</h1>
          <p className="text-slate-400 mt-1 text-sm">HMRC HS205 Calculation - Exact Method</p>
        </div>
        <Button onClick={exportHS205} variant="outline" className="border-slate-600 w-full sm:w-auto">
          <Download className="h-4 w-4 mr-2" />
          Export HS205 Worksheet
        </Button>
      </div>

      {/* Days in Hand - Starting Balance */}
      <DaysInHand />

      {/* Info Banner */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-300">
              <p className="font-medium mb-1">This page shows your eligibility using HMRC's exact HS205 calculation method.</p>
              <p className="text-blue-400/80">
                The "Days In Hand" shown on the Trips page is your running balance. This page shows the official calculation 
                that HMRC uses to determine if you qualify for the Seafarer's Earnings Deduction.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Claim Status */}
        <Card className={`${has_valid_claim ? 'bg-emerald-500/10 border-emerald-500/30' : hasBreach ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              {has_valid_claim ? (
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              ) : hasBreach ? (
                <XCircle className="h-8 w-8 text-red-400" />
              ) : (
                <AlertCircle className="h-8 w-8 text-amber-400" />
              )}
              <div>
                <p className="text-sm text-slate-400">SED Claim Status</p>
                <p className={`text-xl font-bold ${has_valid_claim ? 'text-emerald-400' : hasBreach ? 'text-red-400' : 'text-amber-400'}`}>
                  {has_valid_claim ? 'Eligible' : hasBreach ? 'Claim Broken' : 'Building Period'}
                </p>
                {tableRestarts > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-xs text-amber-400/80 mt-1 cursor-help inline-flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Claim broke {tableRestarts} time{tableRestarts > 1 ? 's' : ''} before current period
                          {periodStart && ` (restarted ${format(parseISO(periodStart), 'dd MMM yyyy')})`}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-slate-900 border-slate-700 text-slate-200 max-w-xs">
                        <p>The HS205 half-day rule was breached {tableRestarts} time{tableRestarts > 1 ? 's' : ''} before the algorithm found a valid starting point for your current period. This means there were gaps where no eligible 365-day period could be constructed.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 365 Day Progress */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-400" />
                <span className="text-sm text-slate-400">365-Day Target</span>
              </div>
              <span className="text-lg font-bold text-white">{totalDays} days</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${progressTo365 >= 100 ? 'bg-emerald-500' : 'bg-purple-500'}`}
                style={{ width: `${progressTo365}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {progressTo365 >= 100 ? '365+ days achieved' : `${365 - totalDays} days to go`}
            </p>
          </CardContent>
        </Card>

        {/* Half-Day Rule Status */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Plane className="h-5 w-5 text-cyan-400" />
                <span className="text-sm text-slate-400">Half-Day Rule</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-slate-500" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-slate-800 border-slate-700">
                      <p>UK days must stay below half of your total days. If exceeded, your eligible period ends.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <span className={`text-lg font-bold ${halfDayBuffer > 30 ? 'text-cyan-400' : halfDayBuffer > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                {Math.round(halfDayBuffer)} days in hand
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${halfDayBuffer <= 0 ? 'bg-red-500' : halfDayBuffer <= 30 ? 'bg-amber-500' : 'bg-cyan-500'}`}
                style={{ width: `${Math.min(halfDayUsed, 100)}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {ukDays} UK days / {Math.round(halfTotal)} max allowed
            </p>
          </CardContent>
        </Card>

        {/* Claim Break Date */}
        <Card data-testid="claim-break-date-card" className={`${!cbd ? 'bg-slate-800/50 border-slate-700' : hasBreach ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-400" />
                <span className="text-sm text-slate-400">Claim Breaks On</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-slate-500" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-slate-800 border-slate-700">
                      <p>Projected date your claim would break if you stay continuously in the UK from your last return. Based on the earlier of the half-day rule or 183-day consecutive limit.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            {cbd ? (
              <>
                <p className="text-lg font-bold text-orange-400">{format(parseISO(cbd.date), 'dd MMM yyyy')}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {cbd.days_from_last_return} days from last return
                  {cbd.reason === 'half_day' ? ' (half-day rule)' : ' (183-day limit)'}
                </p>
              </>
            ) : hasBreach ? (
              <p className="text-sm text-red-400 font-medium">Claim already broken</p>
            ) : (
              <p className="text-sm text-slate-500">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Warnings and Next Steps */}
      {(warnings.length > 0 || next_steps.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {warnings.length > 0 && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-amber-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  {warnings.map((warning, idx) => (
                    <li key={`warning-${idx}`} className="text-sm text-amber-300">{warning}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          
          {next_steps.length > 0 && (
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-blue-400 text-sm flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Next Steps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  {next_steps.map((step, idx) => (
                    <li key={`step-${idx}`} className="text-sm text-blue-300">{step}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Eligible Periods */}
      {(current_period || completed_periods.length > 0) && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-400" />
              Eligible Periods
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {current_period && (
                <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="bg-emerald-500/20 text-emerald-400 mb-2">Current Period</Badge>
                      <p className="text-white font-medium">
                        {format(parseISO(current_period.start_date), 'dd MMM yyyy')} → {format(parseISO(current_period.end_date), 'dd MMM yyyy')}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        {current_period.total_days} total days • {current_period.days_abroad} abroad • {current_period.days_in_uk} UK
                      </p>
                    </div>
                    {current_period.is_valid && (
                      <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                    )}
                  </div>
                </div>
              )}
              
              {completed_periods.map((period, idx) => (
                <div key={`${period.start_date}-${period.end_date}`} className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="bg-slate-600 text-slate-300 mb-2">
                        Completed {period.ended_reason === 'half_day_breach' ? '(Half-day breach)' : period.ended_reason === '183_day_breach' ? '(183-day breach)' : ''}
                      </Badge>
                      <p className="text-white font-medium">
                        {format(parseISO(period.start_date), 'dd MMM yyyy')} → {format(parseISO(period.end_date), 'dd MMM yyyy')}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        {period.total_days} total days • {period.days_abroad} abroad • {period.days_in_uk} UK
                      </p>
                    </div>
                    {period.is_valid ? (
                      <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tax Year Claim Breakdown */}
      {tax_year_claims && tax_year_claims.length > 0 && (
        <Card data-testid="tax-year-claims-section" className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PoundSterling className="h-5 w-5 text-amber-400" />
              Tax Year Claim Breakdown
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-slate-500" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm bg-slate-800 border-slate-700">
                    <p>Shows how your eligible period maps to each tax year. The first tax year may be a partial claim if your eligible period started after April 6th.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[...tax_year_claims].reverse().map((claim, idx) => (
                <div
                  key={claim.tax_year}
                  data-testid={`tax-year-claim-${claim.tax_year}`}
                  className={`p-4 rounded-lg border ${
                    claim.claim_type === 'Partial'
                      ? 'bg-amber-500/5 border-amber-500/20'
                      : 'bg-amber-500/5 border-amber-500/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-semibold">{claim.tax_year}</span>
                          <Badge className={`text-xs ${
                            claim.claim_type === 'Partial'
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {claim.claim_type}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400">
                          {format(parseISO(claim.period_start), 'dd MMM yyyy')} → {format(parseISO(claim.period_end), 'dd MMM yyyy')}
                          <span className="text-slate-500 ml-2">({claim.days_covered} days)</span>
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-400">
                        {claim.qualifying_earnings > 0 ? `£${claim.qualifying_earnings.toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                      </p>
                      <p className="text-xs text-slate-500">qualifying earnings</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* HS205 Working Sheet */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Ship className="h-5 w-5 text-blue-400" />
            HS205 Working Sheet
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-slate-500" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm bg-slate-800 border-slate-700">
                  <p>This is the official HMRC worksheet format. Column H shows if the half-day rule is breached.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {hs205_rows.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No qualifying trips recorded yet</p>
          ) : (
            <div className="max-h-[600px] overflow-auto" data-testid="hs205-table-container">
              <table className="w-full caption-bottom text-xs sm:text-sm min-w-[700px]">
                <thead className="sticky top-0 z-10 bg-slate-800 shadow-[0_1px_0_0_theme(colors.slate.700)]">
                  <tr className="border-b border-slate-700">
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-400 bg-slate-800">Row</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-400 bg-slate-800">Trip</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-400 bg-slate-800">Left UK (A)</th>
                    <th className="h-10 px-2 text-left align-middle font-medium text-slate-400 bg-slate-800">Returned (B)</th>
                    <th className="h-10 px-2 text-right align-middle font-medium text-slate-400 bg-slate-800">Abroad (C)</th>
                    <th className="h-10 px-2 text-right align-middle font-medium text-slate-400 bg-slate-800">UK Days (D)</th>
                    <th className="h-10 px-2 text-right align-middle font-medium text-slate-400 bg-slate-800">Total (E)</th>
                    <th className="h-10 px-2 text-right align-middle font-medium text-slate-400 bg-slate-800">UK Total (F)</th>
                    <th className="h-10 px-2 text-right align-middle font-medium text-slate-400 bg-slate-800">Half (G)</th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-slate-400 bg-slate-800">Breach (H)</th>
                  </tr>
                </thead>
                <tbody>
                  {hs205_rows.map((row, idx) => {
                    const prevRow = idx > 0 ? hs205_rows[idx - 1] : null;
                    const isNewTable = prevRow && row.total_days < prevRow.total_days;
                    
                    return (
                      <React.Fragment key={row.row_num}>
                        {isNewTable && (
                          <tr className="border-b border-slate-600">
                            <td colSpan={10} className="py-2 px-4 bg-slate-700/30">
                              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                                Claim break — HS205 restarting from new period (Step 10)
                              </p>
                            </td>
                          </tr>
                        )}
                        <tr 
                          className={`border-b border-slate-700 transition-colors hover:bg-slate-700/50 ${row.breach || row.breach_183 ? 'bg-red-500/10' : row.total_days >= 365 ? 'bg-amber-500/5' : ''}`}
                        >
                          <td className="p-2 align-middle text-slate-300">{row.row_num}</td>
                          <td className="p-2 align-middle text-white font-medium">{row.trip_name}</td>
                          <td className="p-2 align-middle text-slate-300">{format(parseISO(row.date_left_uk), 'dd/MM/yy')}</td>
                          <td className="p-2 align-middle text-slate-300">{format(parseISO(row.date_returned_uk), 'dd/MM/yy')}</td>
                          <td className="p-2 align-middle text-right text-amber-400">{row.days_abroad}</td>
                          <td className={`p-2 align-middle text-right ${row.breach_183 ? 'text-red-400 font-bold' : 'text-amber-400'}`}>
                            {row.days_in_uk}
                            {row.breach_183 && <span className="text-xs ml-1">(183+)</span>}
                          </td>
                          <td className={`p-2 align-middle text-right font-medium ${row.total_days >= 365 ? 'text-amber-400' : 'text-white'}`}>
                            {row.total_days}
                          </td>
                          <td className="p-2 align-middle text-right text-slate-300">{row.total_uk_days}</td>
                          <td className="p-2 align-middle text-right text-slate-400">{row.half_total}</td>
                          <td className="p-2 align-middle text-center">
                            {row.breach ? (
                              <Badge className="bg-red-500/20 text-red-400">YES</Badge>
                            ) : (
                              <Badge className="bg-slate-600 text-slate-300">NO</Badge>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* HMRC Reference */}
      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardContent className="p-4">
          <p className="text-xs text-slate-500">
            This calculation follows HMRC's HS205 Seafarers' Earnings Deduction helpsheet. 
            For official guidance, see{' '}
            <a 
              href="https://www.gov.uk/government/publications/seafarers-earnings-deduction-hs205-self-assessment-helpsheet" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
              GOV.UK HS205
            </a>. 
            Always consult a qualified tax adviser for your specific situation.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Eligibility;
